import { Button, Stack, Typography } from '@compass/components'
import { Link } from '@mui/material'
import type { Route as EventsRoute } from '@routes/_authenticated/_layout/workspaces/$subscriptionId/events/'
import { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { AppRoute } from 'appRoutes'
import { DOCS_SERVER_API, GenericError } from 'const'
import { ContentColumn, MainColumn, SetHeader, SubHeader } from 'features/commonUI'
import { BatchLabelToolbar } from 'features/fraudAnalysis'
import { useCurrentSubscription } from 'features/subscription'
import { useFraudAnalysisAccess } from 'hooks/useFraudAnalysisAccess'
import intersection from 'lodash/intersection'
import { VisitsFilterKey } from 'models'
import { IdEventsResponseV4 } from 'models/event'
import { useCallback, useMemo } from 'react'

import { useCurrentUser } from '../../../../hooks/api/users'
import { useFilterSearchParams } from '../../hooks/useFilterSearchParams'
import { useVisitsFilter } from '../../hooks/useVisitsFilter'
import { ActionsMenu } from '../ActionsMenu'
import { AppliedFilters } from '../AppliedFilters/AppliedFilters'
import { VisitHistoryFilter } from '../VisitHistoryFilter/VisitHistoryFilter'
import { VisitsTableNew } from '../VisitsTable/VisitsTableNew'
import styles from './VisitHistory.module.scss'

const identificationEventsPath = `${AppRoute.IdentificationEvents}/` satisfies typeof EventsRoute.fullPath

export type VisitHistoryProps = {
  query: UseInfiniteQueryResult<InfiniteData<IdEventsResponseV4>, GenericError>
}

export function VisitHistory({ query }: VisitHistoryProps) {
  const navigate = useNavigate({ from: identificationEventsPath })
  const { currentSubscriptionId } = useCurrentSubscription()
  const { resetSearchParams, filterValues } = useFilterSearchParams()
  const { filterItems, disabledFilterKeys } = useVisitsFilter()
  const { data: currentUser } = useCurrentUser()
  const { hasAccess: canLabelEvents } = useFraudAnalysisAccess()

  const isSearchResults = (query.data?.pages?.[0]?.events?.length ?? 0) > 0
  const appliedFilterCount = intersection(Object.keys(filterValues), Object.keys(filterItems)).filter(
    (key) => !disabledFilterKeys.has(key)
  ).length

  // Batch-label selection survives pagination and page size, but not a different result set.
  const selectionScope = useMemo(() => {
    const { limit: _pageSize, ...resultSet } = filterValues
    // Sorted, so the same filters in a different URL order are the same result set.
    return JSON.stringify([currentSubscriptionId, Object.entries(resultSet).sort(([a], [b]) => a.localeCompare(b))])
  }, [currentSubscriptionId, filterValues])

  const handleClearPress = useCallback(
    () => resetSearchParams(Object.keys(filterItems) as VisitsFilterKey[]),
    [filterItems, resetSearchParams]
  )

  return (
    <>
      <SetHeader title='Identification' />
      <MainColumn>
        <SubHeader
          keepRowLayout
          title={
            <Typography variant='heading' size='2xl' as='h2'>
              Identification events
            </Typography>
          }
          description={
            <>
              <Typography size='base' className='text-gray-800'>
                Select a row for details. Events data is also available by&nbsp;
                <Link href={DOCS_SERVER_API} target='_blank' underline='hover'>
                  server API
                </Link>
                .
              </Typography>
            </>
          }
          actions={<ActionsMenu isExportDisabled={query.isFetching || !isSearchResults} />}
        />
        <ContentColumn>
          <Stack gap={4} direction={{ default: 'column', sm: 'row' }} className='justify-between'>
            <Stack gap={3} direction={{ default: 'column', sm: 'row' }}>
              <VisitHistoryFilter
                timezone={currentUser?.timezone}
                subscriptionId={currentSubscriptionId}
                appliedFilterCount={appliedFilterCount}
              />
            </Stack>
            {appliedFilterCount > 0 && (
              <Button
                data-testid='visit-history-filter-clear-button'
                aria-label='Clear filters'
                variant='ghost'
                tone='background2'
                alt
                className='hidden md:flex'
                onPress={handleClearPress}
              >
                Clear
              </Button>
            )}
          </Stack>
          <AppliedFilters />
          {appliedFilterCount > 0 && (
            <Button
              aria-label='Clear filters'
              variant='outline'
              tone='background2'
              alt
              className='md:hidden'
              onPress={handleClearPress}
            >
              Clear
            </Button>
          )}
          <VisitsTableNew
            withContainer
            query={query}
            className={styles.tableContainer}
            onClear={handleClearPress}
            onLimitChange={(limit) => navigate({ to: '.', search: (old) => ({ ...old, limit }) })}
            appliedFilterCount={appliedFilterCount}
            selectionScope={selectionScope}
            renderSelectionActions={
              canLabelEvents
                ? (selectionProps) => <BatchLabelToolbar workspaceId={currentSubscriptionId} {...selectionProps} />
                : undefined
            }
          />
        </ContentColumn>
      </MainColumn>
    </>
  )
}
